import { css, styled } from 'styled-components'

import { isConsole } from '../../config/config'
import { formatCurrency, formatDateTime } from '../../helpers/formatters'
import { Handler } from '../../helpers/typeHelper'
import { useBreakpoint } from '../../hooks/useBreakpoint'
import { PolicyStatusText, Repayment, RepaymentPolicyStatus } from '../../models/repayments'
import { dividerGray, midGray, primaryBlack } from '../../theme/colors'
import { FlexBox } from '../Base/FlexBox'
import { Grid } from '../Base/Grid'
import { TextNoOverflow } from '../Base/TextFormat'
import { Text } from '../Text/Text'
import { getPolicyHeader } from './Policy.helper'
import { PolicyStatusBadge } from './PolicyStatusBadge'

interface PolicyRepaymentsListProps {
  repayments: Repayment[]
  onRowClick: (row: Repayment) => void
}

export function PolicyRepaymentsList({ repayments, onRowClick }: PolicyRepaymentsListProps) {
  const isMd = useBreakpoint('md')
  const isSm = useBreakpoint('sm')
  const isXs = useBreakpoint('xs')

  return (
    <>
      <Row isHeader>
        <Text variant="header4" color={midGray}>
          Deduction name
        </Text>
        {!isConsole() && !isXs && (
          <Text variant="header4" color={midGray}>
            Fleet name
          </Text>
        )}
        {(!isMd || isConsole()) && (
          <Text variant="header4" color={midGray}>
            Start date
          </Text>
        )}
        {(!isMd || isConsole()) && (
          <Text variant="header4" color={midGray}>
            Current balance
          </Text>
        )}
        {(!isSm || isConsole()) && (
          <Text variant="header4" color={midGray}>
            Overdue balance
          </Text>
        )}
        {isConsole() && (
          <>
            <Text variant="header4" color={midGray}>
              Next billing date
            </Text>
            <Text variant="header4" color={midGray}>
              Next billing amount
            </Text>
          </>
        )}
        <Text variant="header4" color={midGray}>
          Status
        </Text>
      </Row>
      {repayments.map((r) => {
        const status = r.repayment_details.policy_status ?? RepaymentPolicyStatus.DRAFT

        return (
          <Row onClick={() => onRowClick(r)} key={r.repayment_details.policy_id}>
            <Text variant="label2" color={primaryBlack}>
              <TextNoOverflow>{getPolicyHeader(r)}</TextNoOverflow>
            </Text>
            {!isConsole() && !isXs && (
              <Text variant="label2" color={primaryBlack}>
                {r.repayment_details.team_name}
              </Text>
            )}
            {(!isMd || isConsole()) && (
              <Text variant="label2" color={primaryBlack}>
                {formatDateTime(r.repayment_details.payment_start_date)}
              </Text>
            )}
            {(!isMd || isConsole()) && (
              <Text variant="label2" color={primaryBlack}>
                {formatCurrency(r.repayment_details.remaining_payment_this_period)}
              </Text>
            )}
            {(!isSm || isConsole()) && (
              <Text variant="label2" color={primaryBlack}>
                {formatCurrency(r.repayment_details.underpayment_from_previous_periods_as_of_today)}
              </Text>
            )}
            {isConsole() && (
              <>
                <Text variant="label2" color={primaryBlack}>
                  {formatDateTime(r.repayment_details.next_billing_date || '')}
                </Text>
                <Text variant="label2" color={primaryBlack}>
                  {formatCurrency(
                    (
                      parseFloat(r.repayment_details.amount_owed_per_period ?? 0) +
                      parseFloat(r.repayment_details.underpayment_from_previous_periods ?? 0)
                    ).toFixed(2)
                  )}
                </Text>
              </>
            )}
            <div>
              <FlexBox>
                <PolicyStatusBadge $status={RepaymentPolicyStatus[status]}>
                  <Text variant="header4">{PolicyStatusText[status]}</Text>
                </PolicyStatusBadge>
              </FlexBox>
            </div>
          </Row>
        )
      })}
    </>
  )
}

function Row({
  children,
  isHeader,
  onClick,
}: {
  children: React.ReactNode
  isHeader?: boolean
  onClick?: Handler
}) {
  const isMd = useBreakpoint('md')
  const isSm = useBreakpoint('sm')
  const isXs = useBreakpoint('xs')

  function getColumns() {
    if (isConsole()) return 'minmax(170px, 1fr) 200px 200px 200px 200px 150px 1fr'
    if (isXs) return '1fr 1fr'
    if (isSm) return '150px 150px 200px'
    if (isMd) return '170px 300px 1fr 200px'
    return 'minmax(170px, 1fr) 200px 200px 110px 120px 200px'
  }

  return (
    <StyledGrid columns={getColumns()} gap="10px" $isHeader={isHeader} onClick={onClick}>
      {children}
    </StyledGrid>
  )
}

const StyledGrid = styled(Grid)<{ $isHeader?: boolean }>`
  padding: 24px;
  margin-top: 8px;
  background-color: white;
  cursor: pointer;

  ${isConsole() &&
  css`
    padding: 12px 24px;
    border-bottom: 1px solid ${dividerGray};
  `}

  ${(p) =>
    p.$isHeader &&
    css`
      background-color: transparent;
      padding-top: 5px;
      padding-bottom: 5px;
    `}
`
